import React, { Fragment, PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Recaptcha from 'react-google-invisible-recaptcha';
import { RECAPTCHA_SECRET } from '../../common/globals';
import markdownToSafeHtml from '../../common/markdownToSafeHtml';

import FormViewer from '../../FormViewer';
import shapes from '../../common/shapes';
// Form header image is now rendered inside FormViewer via ObjectFieldTemplate
import FormFooter from './formFooter';

import './formClientContent.css';

class FormClientContent extends PureComponent {
  constructor(props) {
    super(props);
    
    this.state = {
      form: {},
      validationError: null,
    };

    this.recaptcha = createRef();
  }

  handleValidationError = () => {
    this.setState({ validationError: 'Please fill in all required fields before submitting.' });
  };

  handleChange = () => {
    if (this.state.validationError) {
      this.setState({ validationError: null });
    }
  };

  // Instead of submitting, check recaptcha
  handleSubmit = (form) => {
    const isTeamForm = get(window, '__INITIAL_DATA__.user.team.name');

    if (isTeamForm) {
      const {
        onSubmit
      } = this.props;

      onSubmit(form);
    }
    else {
      this.setState({
        form
      });

      this.recaptcha.current.execute();
    }
  }

  onResolved = (captchaToken) => {
    const {
      onSubmit
    } = this.props;

    const {
      form
    } = this.state;

    onSubmit({
      ...form,
      captchaToken
    });
  };

  render() {
    const {
      userName,
      schema,
      uiSchema,
      isSubmitting,
      isSubmitted,
      submitErrorMessage,
      headerImage,
      confirmationText,
      buttonLabel,
      buttonColor
    } = this.props;

    const isTeamForm = get(window, '__INITIAL_DATA__.user.team.name');
    // Success screen uses the same hero styles as the form
    const tickCircleStyle = {
      width: 56,
      height: 56,
      borderRadius: '50%',
      background: 'transparent',
      border: '2px solid #000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };
    const confirmationDescriptionStyle = { marginTop: 8 };

    const lighten = (hex, factor = 0.9) => {
      const normalized = hex ? hex.replace('#', '') : '5986ff';
      const full = normalized.length === 3
        ? normalized.split('').map(c => c + c).join('')
        : normalized;
      const r = parseInt(full.substring(0, 2), 16);
      const g = parseInt(full.substring(2, 4), 16);
      const b = parseInt(full.substring(4, 6), 16);
      const mix = c => Math.round(c + (255 - c) * factor);
      const toHex = n => n.toString(16).padStart(2, '0');
      return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
    };

    const heroStyle = headerImage ? {} : { backgroundColor: lighten(buttonColor, 0.92) };
    const cloudinaryBase = 'https://res.cloudinary.com/dqlpbfemc/image/fetch/t_form_banner';
    const transformedHeaderImage = headerImage
      ? `${cloudinaryBase}/${encodeURI(headerImage)}`
      : '';

    const content = isSubmitted
      ? (
        <div className="form-viewer">
          <div className="form-hero" style={heroStyle}>
            {headerImage && (
              <div className="form-hero__image">
                <img alt="hero" src={transformedHeaderImage} />
              </div>
            )}
            <div className="form-hero__content">
              <div style={{ display: 'flex', justifyContent: 'left', margin: '16px 0' }}>
                <div style={tickCircleStyle}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M7 12.5L11 16.5L17.5 9" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              {confirmationText && confirmationText.trim() !== '' && (
                <div
                  className="form-hero__description"
                  style={confirmationDescriptionStyle}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: markdownToSafeHtml(confirmationText)
                  }}
                />
              )}
              {(!confirmationText || confirmationText.trim() === '') && (
                <h1 className="form-hero__title">Your submission has been received</h1>
              )}
            </div>
          </div>
          <FormFooter />
        </div>
      )
      : (
        <Fragment>
          <FormViewer
            schema={schema}
            uiSchema={uiSchema}
            formContext={{
              headerImage,
              buttonLabel,
              buttonColor,
              cloudinaryCloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
              cloudinaryUploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
            }}
            onSubmit={this.handleSubmit}
            onError={this.handleValidationError}
            onChange={this.handleChange}
            isSubmitting={isSubmitting}
          />
          {(submitErrorMessage || this.state.validationError) && (
          <div className="form-submit-error" role="alert">
            <svg className="form-submit-error__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M8 5.5V8.5M8 10.5H8.007M6.343 2.343l-4 4a2 2 0 000 2.828l4 4a2 2 0 002.828 0l4-4a2 2 0 000-2.828l-4-4a2 2 0 00-2.828 0z" stroke="#b42318" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {submitErrorMessage || this.state.validationError}
          </div>
          )}
          {userName && (
          <p className="text-center user-name">
            <strong>
              You are submitting this form as
              {' '}
              {userName}
            </strong>
          </p>
          )}
          {!isTeamForm && (
            <Recaptcha
              ref={this.recaptcha}
              sitekey={RECAPTCHA_SECRET}
              onResolved={this.onResolved}
            />
          )}
          <FormFooter />
        </Fragment>
      );
  
    return (
      <Fragment>
        {content}
      </Fragment>
    );
  }
}

FormClientContent.defaultProps = {
  userName: '',
  headerImage: '',
  confirmationText: '',
  buttonLabel: 'Submit',
  buttonColor: '',
  submitErrorMessage: ''
};

FormClientContent.propTypes = {
  userName: PropTypes.string,
  headerImage: PropTypes.string,
  confirmationText: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonColor: PropTypes.string,
  submitErrorMessage: PropTypes.string,
  schema: PropTypes.shape(shapes.JSONSchema).isRequired,
  uiSchema: PropTypes.shape(shapes.UISchema).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isSubmitted: PropTypes.bool.isRequired
};

export default FormClientContent;
